import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clustersAPI } from '../services/api';
import websocket from '../services/websocket';
import { useAuth } from '../context/AuthContext';

function Clusters() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  // cluster id -> true if ANY StorageClass on it has a confirmed-broken provisioner.
  // Fetched client-side, once, on list load — no background job/scheduler (explicitly
  // scoped out), just the same on-demand check the cluster detail page already offers,
  // surfaced here too so admins see it without opening each cluster.
  const [storageWarnings, setStorageWarnings] = useState({});

  useEffect(() => {
    let connectedIds = [];
    const load = async () => {
      try {
        const response = await clustersAPI.list();
        setClusters(response.data);
        setLoading(false);
        // Only connect WS for clusters that are actively syncing (not imported/agent-only)
        response.data.forEach((cluster) => {
          websocket.connect(`cluster_${cluster.id}`, handleClusterUpdate);
          connectedIds.push(cluster.id);
        });
        // Storage health is Rancher/Steve-only (see storage_health.py) — only ask for
        // clusters that can actually answer it, and never let one slow/failing check
        // block the others.
        response.data
          .filter((c) => c.connection_type === 'rancher')
          .forEach((c) => {
            clustersAPI.getStorageHealth(c.id)
              .then((r) => {
                const broken = (r.data.storage_classes || []).some((sc) => sc.healthy === false);
                setStorageWarnings((prev) => ({ ...prev, [c.id]: broken }));
              })
              .catch(() => { /* no verdict available — show no badge rather than a false one */ });
          });
      } catch (error) {
        console.error('Failed to load clusters:', error);
        setLoading(false);
      }
    };
    load();
    // Disconnect all WebSocket channels when leaving the Clusters page
    return () => {
      connectedIds.forEach((id) => websocket.disconnect(`cluster_${id}`));
    };
  }, []);

  const handleClusterUpdate = (data) => {
    if (data.type === 'status') {
      setClusters((prevClusters) =>
        prevClusters.map((cluster) =>
          cluster.id === data.resource_id
            ? { ...cluster, status: data.status }
            : cluster
        )
      );
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Clusters</h1>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={() => navigate('/clusters/add')}>
            + Add Cluster
          </button>
        )}
      </div>
      <div className="table-card">
        <table className="ds">
          <thead>
            <tr>
              <th>Name</th>
              <th>Environment</th>
              <th>Version</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clusters.map((cluster) => (
              <tr key={cluster.id}>
                <td>
                  {cluster.name}
                  {storageWarnings[cluster.id] && (
                    <span
                      title="At least one storage class on this cluster has no working storage provisioner — deploys using it will hang. Open the cluster for details."
                      style={{ marginLeft: 8, color: 'var(--color-warning-500)', fontSize: 14, cursor: 'help' }}
                    >
                      ⚠
                    </span>
                  )}
                </td>
                <td>{cluster.environment}</td>
                <td>{cluster.version || 'N/A'}</td>
                <td>
                  {/* An agent cluster derived to 'pending' = its agent is unreachable
                      (it was provisioned, so 'pending' really means offline). Show
                      'Unreachable' so it doesn't read like it's still setting up. */}
                  {cluster.connection_type === 'agent' && cluster.status === 'pending' ? (
                    <span className="status-badge status-failed"
                      title="Agent offline — live status can't be verified">
                      Unreachable
                    </span>
                  ) : (
                    <span className={`status-badge status-${cluster.status}`}>
                      {cluster.status}
                    </span>
                  )}
                </td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/clusters/${cluster.id}`)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Clusters;
